/**
 * screens/modals/TransferCustodyModal
 * Hand an assignment to someone else. The balance moves with it — this is a
 * change of custodian, not a payment, so nothing is added to or taken from the
 * ledger's total.
 */
import { useMemo, useState } from 'react';
import { ChipRow, Input, Modal } from '../../components';
import { sentenceCase } from '../../lib/format';
import { transferCustody } from '../../services/cash/cash.service';
import { useConfig } from '../../context/ConfigContext';
import { useShell } from '../../context/ShellContext';
import { useModalSubmit } from './useModalSubmit';

export interface TransferCustodyModalProps {
  assignmentId: string;
}

export function TransferCustodyModal({ assignmentId }: TransferCustodyModalProps) {
  const { closeModal } = useShell();
  const { optionsByKind } = useConfig();

  const [toHolder, setToHolder] = useState('');
  const [toRef, setToRef] = useState('');
  const [toType, setToType] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const holderTypes = useMemo(
    () =>
      optionsByKind.holderType
        .filter((row) => row.is_active)
        .map((row) => ({ value: row.value, label: row.display_name || sentenceCase(row.value) })),
    [optionsByKind],
  );

  const { submitting, run } = useModalSubmit({
    validate: () => {
      if (!toHolder.trim()) return 'Who is taking custody?';
      if (!toRef.trim()) return 'The new holder needs a reference.';
      if (!toType && holderTypes.length === 0) return 'Add a holder type in Settings first.';
      return null;
    },
    submit: () =>
      transferCustody(assignmentId, {
        to_holder: toHolder.trim(),
        to_ref: toRef.trim(),
        to_type: toType ?? holderTypes[0]!.value,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      }),
    title: 'Custody transferred',
    fallback: `${toHolder.trim() || 'The new holder'} now holds this cash.`,
  });

  return (
    <Modal
      visible
      title="Transfer custody"
      hint="The assignment and its balance move to a new holder. Nothing is spent."
      onClose={closeModal}
      primaryLabel="Transfer custody"
      onPrimary={run}
      primaryLoading={submitting}
    >
      <Input label="New holder" required value={toHolder} onChangeText={setToHolder} placeholder="Asha R." />
      <Input
        label="New holder reference"
        required
        value={toRef}
        onChangeText={setToRef}
        placeholder="ppl_9204"
        monospace
      />
      <ChipRow
        label="Holder type"
        required
        options={holderTypes}
        value={toType ?? holderTypes[0]?.value ?? null}
        onChange={setToType}
      />
      <Input
        label="Reason"
        hint="Shown on the assignment's custody history."
        value={reason}
        onChangeText={setReason}
        placeholder="Shift handover"
      />
    </Modal>
  );
}

export default TransferCustodyModal;
